/**
 * The org bucket's upload surface (docs/ORG_TENANCY_SPEC.md): the org
 * lives at the BUCKET level, the user at the PATH level -- uploads land
 * at users/{uid}/uploads/{filename}, authorized entirely by Firebase
 * Storage rules (no backend in the upload path).
 *
 * This IS the ingest trigger: the object's OBJECT_FINALIZE event feeds
 * the worker's pull loop (v1 slice box 5), so no backend sits in the
 * upload path.
 *
 * Dev note: the compose stack runs only the AUTH emulator, so emulator
 * users cannot authenticate against the real bucket -- bucket upload is
 * a no-op there (null return), and dev's api upload stays the fallback
 * trigger.
 */
import { getStorage, ref, uploadBytesResumable, type UploadTask } from 'firebase/storage';

import { auth, firebaseApp } from '../../api';

// The first org's bucket, pinned at build time like the tenant id
// (runtime org resolution arrives with wildcard DNS).
const ORG_BUCKET: string = import.meta.env.VITE_ORG_BUCKET ?? '';

export function orgBucketConfigured(): boolean {
  return ORG_BUCKET !== '' && auth.tenantId !== null;
}

/** Start the bucket upload; returns null when not configured (dev).
 * The project lives in the PATH -- box 5's addressing contract: the
 * worker's pull loop reads it from the object name. */
export function uploadToOrgBucket(file: File, projectId: string): UploadTask | null {
  const user = auth.currentUser;
  if (!orgBucketConfigured() || user === null) return null;
  const storage = getStorage(firebaseApp, `gs://${ORG_BUCKET}`);
  return uploadBytesResumable(
    ref(storage, `users/${user.uid}/uploads/${projectId}/${file.name}`),
    file,
    { contentType: 'application/octet-stream' },
  );
}
